import { LayoutGrid, Rows3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger } from 'v5/design-system/Tabs';
import { cn } from 'v5/platform/dom/cn';

export type ViewSwitchValue = 'list' | 'grid';

export interface ViewSwitchProps {
  value: ViewSwitchValue;
  onChange: (value: ViewSwitchValue) => void;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * The List / Grid switch, as icons. In a toolbar that gives it room (`@container/toolbar`, 48rem
 * and wider) each icon has its word beside it; the accessible names are the same either way.
 */
export function ViewSwitch({
  value,
  onChange,
  size = 'md',
  className
}: ViewSwitchProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ViewSwitch' });
  return (
    <Tabs
      value={value}
      onValueChange={next => {
        if (next === 'list' || next === 'grid') onChange(next);
      }}
      className={className}
    >
      <TabsList variant="default" size={size}>
        <TabsTrigger
          value="list"
          size={size}
          aria-label={t('listView')}
          // one line as tall as the icon: the word doesn't grow the tab out of its list
          className={cn(
            'gap-1.5 leading-none',
            size === 'sm' ? 'p-1.5 @3xl/toolbar:px-2' : 'p-2'
          )}
        >
          <Rows3 className="size-4" />
          <span className="sr-only @3xl/toolbar:not-sr-only">{t('list')}</span>
        </TabsTrigger>
        <TabsTrigger
          value="grid"
          size={size}
          aria-label={t('gridView')}
          className={cn(
            'gap-1.5 leading-none',
            size === 'sm' ? 'p-1.5 @3xl/toolbar:px-2' : 'p-2'
          )}
        >
          <LayoutGrid className="size-4" />
          <span className="sr-only @3xl/toolbar:not-sr-only">{t('grid')}</span>
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
}
